import React from 'react';
import moment from 'moment';
import { Badge } from 'reactstrap';
import {
  FaBan,
  FaBicycle,
  FaPenNib,
  FaTrophy,
  FaBox,
  FaDoorOpen,
  FaRocket,
  FaCheck,
  FaSignOutAlt,
} from 'react-icons/all';
import {
  OnboardingStatus,
  ServicePackageType,
} from '../../graphql/generatedTypes';
import {
  getServicePackageDisplayName,
  getServicePackageColor,
  getServicePackageTextColor,
} from '@/utils/servicePackage';

type StoreStatusBadgeProps = {
  contracted_date?: string | null;
  contract_end_date?: string | null;
  onboarded_date?: string | null;
};

const isDateValid = (isoDateStr?: string | null) =>
  isoDateStr && !isoDateStr.startsWith('19');
const dateFormat = 'DD.MM.YYYY';

export default function StoreStatusBadge(props: StoreStatusBadgeProps) {
  const { contracted_date, contract_end_date, onboarded_date } = props;

  const color = undefined;
  if (isDateValid(contract_end_date)) {
    const contractEndMoment = moment(contract_end_date);
    const wording = contractEndMoment.isBefore(moment())
      ? 'terminated'
      : 'terminated soon';
    return (
      <Badge pill color={color}>
        <FaBan /> {wording}: {moment(contract_end_date).format(dateFormat)}
      </Badge>
    );
  } else if (isDateValid(onboarded_date)) {
    const onboardedMoment = moment(onboarded_date);
    const wording = onboardedMoment.isBefore(moment())
      ? 'onboarded'
      : 'will onboard';
    return (
      <Badge pill color={color}>
        <FaBicycle /> {wording}: {onboardedMoment.format(dateFormat)}
      </Badge>
    );
  } else if (isDateValid(contracted_date)) {
    const contractedMoment = moment(contracted_date);
    return (
      <Badge pill color={color}>
        <FaPenNib /> contracted: {contractedMoment.format(dateFormat)}
      </Badge>
    );
  } else {
    return null;
  }
}

type AcquiredByBadgeProps = {
  acquired_by?: string;
  acquired_at?: string;
};

export function AcquiredByBadge({
  acquired_at: acquiredAt,
  acquired_by: acquiredBy,
}: AcquiredByBadgeProps) {
  const acquiredByFormatted =
    acquiredBy && acquiredBy.includes('-')
      ? acquiredBy.split('-')[0].trim()
      : acquiredBy;

  const acquiredAtMoment = moment(acquiredAt);
  const acquiredAtFormatted = isDateValid(acquiredAt)
    ? acquiredAtMoment.format(dateFormat)
    : null;

  if (!acquiredAtFormatted && !acquiredByFormatted) {
    return null;
  }

  return (
    <Badge pill>
      <FaTrophy />
      {acquiredByFormatted && ' acquired by: ' + acquiredByFormatted}
      {acquiredAtFormatted && ' at: ' + acquiredAtFormatted}
    </Badge>
  );
}

type ServicePackageBadgeProps = {
  service_package?: ServicePackageType | null;
};

export function ServicePackageBadge({
  service_package,
}: ServicePackageBadgeProps) {
  if (!service_package) {
    return null;
  }

  return (
    <Badge
      pill
      color=""
      style={{
        backgroundColor: getServicePackageColor(service_package),
        color: getServicePackageTextColor(service_package),
        border: 'none',
      }}
    >
      <FaBox /> {getServicePackageDisplayName(service_package)}
    </Badge>
  );
}

type OnboardingStatusBadgeProps = {
  onboarding_status?: OnboardingStatus | null;
};

function getOnboardingStatusConfig(status: OnboardingStatus): {
  label: string;
  color: string;
  icon: React.ReactNode;
} {
  switch (status) {
    case OnboardingStatus.Open:
      return { label: 'Open', color: 'info', icon: <FaDoorOpen /> };
    case OnboardingStatus.WonOnboarding:
      return { label: 'Won/Onboarding', color: 'primary', icon: <FaRocket /> };
    case OnboardingStatus.Active:
      return { label: 'Active', color: 'success', icon: <FaCheck /> };
    case OnboardingStatus.Offboarding:
      return { label: 'Offboarding', color: 'warning', icon: <FaSignOutAlt /> };
    case OnboardingStatus.Terminated:
      return { label: 'Terminated', color: 'danger', icon: <FaBan /> };
    default:
      return { label: status, color: 'secondary', icon: null };
  }
}

export function OnboardingStatusBadge({
  onboarding_status,
}: OnboardingStatusBadgeProps) {
  if (!onboarding_status) {
    return null;
  }

  const { label, color, icon } = getOnboardingStatusConfig(onboarding_status);

  return (
    <Badge pill color={color}>
      {icon} Status: {label}
    </Badge>
  );
}
